import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SiteHeader from '../components/SiteHeader';
import Footer from '../components/Footer';
import useAuthModal from '../hooks/useAuthModal';
import {
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  Star,
  Shield,
  Clock,
  X
} from 'lucide-react';

/* ─────────────────────────────────────────────
   STEP BLOCK — clean text, no cards
   ───────────────────────────────────────────── */
const Step = ({ number, phase, title, subtitle, paragraphs, details, accentColor }) => (
  <div className="py-12 border-b border-slate-100 last:border-0">
    <div className="flex items-baseline gap-4 mb-1">
      <span className={`text-sm font-bold uppercase tracking-widest ${accentColor}`}>{phase}</span>
    </div>

    <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-3 mb-2 tracking-tight">
      Step {number}: {title}
    </h2>

    <p className={`text-base font-semibold mb-4 ${accentColor}`}>{subtitle}</p>

    {paragraphs.map((p, i) => (
      <p key={i} className="text-slate-600 leading-relaxed mb-3 text-base max-w-3xl">{p}</p>
    ))}

    {details && details.length > 0 && (
      <ul className="mt-4 space-y-2 max-w-3xl">
        {details.map((d, i) => (
          <li key={i} className="flex items-start gap-2.5 text-sm text-slate-500">
            <CheckCircle2 size={16} className={`mt-0.5 flex-shrink-0 ${accentColor}`} />
            <span>{d}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

/* ─────────────────────────────────────────────
   SOCIAL PROOF
   ───────────────────────────────────────────── */
const SocialProof = () => (
  <section className="py-16 bg-slate-50 border-y border-slate-100">
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
      <p className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-8 text-center">What beta testers say</p>
      <div className="space-y-6">
        {[
          {
            quote: "First time I felt actually prepared for a system design round. The AI asked follow-ups I didn't expect — exactly like the real thing.",
            name: "Andrei K.",
            detail: "Senior Backend Engineer · Hired at Wise",
          },
          {
            quote: "I've used ChatGPT for mock interviews, but it doesn't know what the company actually asks. JobSkill pulls from the real JD — that's the difference.",
            name: "Maria S.",
            detail: "Product Analyst · Hired at Delivery Hero",
          },
          {
            quote: "Speech pace analysis caught something I never noticed — I speed up on technical answers. Fixed it in 3 sessions.",
            name: "Dmitry V.",
            detail: "Engineering Manager · Hired at Glovo",
          }
        ].map((t, i) => (
          <div key={i} className="bg-white rounded-xl p-5 border border-slate-200">
            <div className="flex gap-0.5 mb-2">
              {[...Array(5)].map((_, j) => <Star key={j} size={13} className="text-amber-400 fill-amber-400" />)}
            </div>
            <p className="text-sm text-slate-600 leading-relaxed italic mb-3">"{t.quote}"</p>
            <p className="text-xs text-slate-400"><span className="font-semibold text-slate-600">{t.name}</span> — {t.detail}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ─────────────────────────────────────────────
   COMPARISON
   ───────────────────────────────────────────── */
const Comparison = () => (
  <section className="py-16 px-4 sm:px-6 lg:px-8">
    <div className="max-w-3xl mx-auto">
      <h2 className="text-2xl font-bold text-slate-900 mb-2 tracking-tight">Why not just use ChatGPT?</h2>
      <p className="text-slate-500 mb-8">General-purpose AI gives generic questions. Here's what it can't do.</p>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="grid grid-cols-3 text-center text-xs font-bold uppercase tracking-wider py-3 bg-slate-50 border-b border-slate-200">
          <div className="text-slate-400 text-left pl-5">Feature</div>
          <div className="text-slate-400">ChatGPT</div>
          <div className="text-orange-600">JobSkill</div>
        </div>
        {[
          'Real vacancy as interview context',
          'Adaptive follow-up questions',
          'Role-specific scoring rubric',
          'Speech pace & filler word analysis',
          'Progress tracking across sessions',
          'Skills gap vs. market demand',
        ].map((feature, i) => (
          <div key={i} className={`grid grid-cols-3 text-center py-3 text-sm border-b border-slate-50 last:border-0 ${i % 2 === 0 ? '' : 'bg-slate-50/30'}`}>
            <div className="text-slate-600 text-left pl-5 font-medium">{feature}</div>
            <div><X size={15} className="text-slate-300 mx-auto" /></div>
            <div><CheckCircle2 size={15} className="text-emerald-500 mx-auto" /></div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ─────────────────────────────────────────────
   FAQ
   ───────────────────────────────────────────── */
const FAQItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-100 last:border-0">
      <button onClick={() => setOpen(!open)} className="flex items-center justify-between w-full py-4 text-left">
        <span className="text-sm font-semibold text-slate-800 pr-4">{q}</span>
        <ChevronDown size={16} className={`text-slate-400 transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="pb-4 text-sm text-slate-500 leading-relaxed -mt-1">{a}</div>}
    </div>
  );
};

const FAQ = () => (
  <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50 border-t border-slate-100">
    <div className="max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold text-slate-900 mb-8 tracking-tight text-center">Common questions</h2>
      <div className="bg-white rounded-xl border border-slate-200 px-5">
        <FAQItem q="How is this different from ChatGPT?" a="ChatGPT gives generic questions. JobSkill pulls the actual job description you're targeting, adapts questions to that role, scores you against a position-specific rubric, and tracks progress over time. Coach who studied your opponent vs. random sparring partner." />
        <FAQItem q="What languages are supported?" a="English and Russian now. Ukrainian and Polish coming Q1 2025. Vacancy database covers major EU and US markets." />
        <FAQItem q="Is my CV data safe?" a="Encrypted at rest and in transit. No sharing with employers or third parties. You can delete everything from profile settings. GDPR-compliant." />
        <FAQItem q="What's in the free tier?" a="One full mock interview with a real vacancy as context, basic scoring report on technical and behavioral answers, and a skills gap analysis. No credit card." />
        <FAQItem q="Non-technical roles?" a="Yes — product management, marketing, finance, analytics, design. Question banks and scoring rubrics adapt to the job description you select." />
      </div>
    </div>
  </section>
);

/* ─────────────────────────────────────────────
   APP
   ───────────────────────────────────────────── */
export default function HowItWorksScreen() {
  const authModal = useAuthModal();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white font-sans text-slate-900 selection:bg-orange-100 selection:text-orange-900">
      <SiteHeader authModal={authModal} />

      <main>
        {/* Hero — concise, instruction-style */}
        <section className="pt-20 pb-16 px-4 bg-gradient-to-b from-white to-slate-50/50">
          <div className="max-w-3xl mx-auto">
            <p className="text-sm font-bold text-orange-600 uppercase tracking-widest mb-4">How it works</p>
            <h1 className="text-3xl md:text-5xl font-extrabold text-slate-900 tracking-tight mb-5 leading-tight">
              From profile to offer — in 5 steps
            </h1>
            <p className="text-lg text-slate-500 leading-relaxed mb-8 max-w-2xl">
              JobSkill uses real job listings as interview context. You pick a vacancy, our AI simulates that company's interview, and you get scored on every answer. Here's exactly how the process works.
            </p>
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/search')}
                className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg font-bold transition-all shadow-md hover:shadow-orange-500/20 flex items-center gap-2 text-sm"
              >
                Try Free Mock Interview <ArrowRight size={16} />
              </button>
              <span className="text-sm text-slate-400 flex items-center gap-1.5">
                <CheckCircle2 size={14} className="text-emerald-500" /> No signup needed
              </span>
            </div>
          </div>
        </section>

        {/* Steps — clean text blocks */}
        <section className="px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto">

          <Step
            number="1"
            phase="Calibration"
            accentColor="text-slate-500"
            title="Create your profile"
            subtitle="Understand your starting position."
            paragraphs={[
              "Upload your CV or fill in your experience manually. The system parses your skills and maps them against current market requirements across 50,000+ active job postings.",
              "You'll immediately see a gap analysis: which of your skills are in demand, which are below market expectations, and which roles you're already competitive for."
            ]}
            details={[
              "Supports PDF, DOCX, and LinkedIn profile import",
              "Skill graph built from your experience + market data",
              "Gap analysis updates as new vacancies appear",
            ]}
          />

          <Step
            number="2"
            phase="Targeting"
            accentColor="text-blue-600"
            title="Pick a real vacancy"
            subtitle="This is what makes JobSkill different."
            paragraphs={[
              "We don't use generic question banks. You browse a live database of open positions sourced from LinkedIn, Glassdoor, and direct employer feeds. Find the role you're actually applying for — or one like it.",
              "Once you select a vacancy, the AI reads the full job description: required skills, seniority level, company context, tech stack. It builds the entire interview around that specific role."
            ]}
            details={[
              "Live listings updated daily",
              "Filter by role, location, seniority, tech stack",
              "AI adapts tone and difficulty to the company's interview style",
            ]}
          />

          <Step
            number="3"
            phase="First session"
            accentColor="text-amber-600"
            title="Take a free mock interview"
            subtitle="Experience the full flow before paying anything."
            paragraphs={[
              "Start a mock interview session — for free, no credit card. The AI Interviewer asks questions relevant to the vacancy you selected. You answer by voice or text.",
              "After the session, you receive a scoring report: technical accuracy, communication clarity, structure of behavioral answers, and specific areas for improvement."
            ]}
            details={[
              "One full-length session (8–12 questions)",
              "Scored report with per-answer breakdown",
              "Completely free — no trial period, no card",
            ]}
          />
        </section>

        {/* Mid CTA */}
        <section className="py-14 px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-8 border border-orange-100 text-center">
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-2">
              That's the free tier. <span className="text-orange-600">Here's what Pro unlocks.</span>
            </h2>
            <p className="text-slate-500 mb-5 text-sm max-w-lg mx-auto">
              One session shows you where you stand. Pro gives you unlimited practice, advanced AI personas, and the analytics to measurably improve.
            </p>
            <button
              onClick={() => navigate('/pricing')}
              className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg font-bold transition-all shadow-md hover:shadow-orange-500/20 inline-flex items-center gap-2 text-sm"
            >
              See Pro Plans <ArrowRight size={16} />
            </button>
          </div>
        </section>

        {/* Steps continued */}
        <section className="px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto">

          <Step
            number="4"
            phase="Deep training"
            accentColor="text-orange-600"
            title="Drill with Pro"
            subtitle="Unlimited sessions. 4 AI personas. Granular analytics."
            paragraphs={[
              "Pro removes all limits. Run as many mock interviews as you need — with different AI personas designed for different preparation goals.",
              "Choose 'Drill Sergeant' for pressure testing. 'Exec Coach' for leadership-style interviews. 'Peer' for casual technical discussions. 'Stress Test' for worst-case scenarios.",
              "Every session feeds into your analytics dashboard: score trends over time, answer-by-answer feedback, speech pace analysis, filler word detection, and comparison to other users at your level."
            ]}
            details={[
              "Unlimited mock interview sessions",
              "4 distinct AI interviewer personas",
              "Answer-by-answer scoring with improvement suggestions",
              "Speech analytics: pace, filler words, confidence markers",
              "Historical progress tracking with trend charts",
            ]}
          />

          <Step
            number="5"
            phase="Outcome"
            accentColor="text-emerald-600"
            title="Walk into the real interview ready"
            subtitle="Their toughest question? You've already answered it. Twice."
            paragraphs={[
              "By the time you sit down with a real recruiter, you've rehearsed this specific interview multiple times. You know the likely questions, you've refined your answers, and you've fixed the weak spots the AI identified.",
              "Early beta users report feeling noticeably more confident and spending less time in their job search pipeline overall. The goal is simple: no surprises on interview day."
            ]}
            details={[
              "Readiness score based on your practice sessions",
              "Salary negotiation preparation module",
              "Exportable preparation report for your own records",
            ]}
          />
        </section>

        {/* Social Proof */}
        <SocialProof />

        {/* Comparison */}
        <Comparison />

        {/* FAQ */}
        <FAQ />

        {/* Final CTA */}
        <section className="py-20 bg-white border-t border-slate-100">
          <div className="max-w-3xl mx-auto text-center px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4 tracking-tight">
              Your next interview is already scheduled. <span className="text-orange-600">Are you ready?</span>
            </h2>
            <p className="text-slate-500 mb-8 max-w-lg mx-auto">
              Every day without practice is a day your competition is getting ahead. Start with a free session on the exact role you want.
            </p>
            <button
              onClick={() => navigate('/search')}
              className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 rounded-lg font-bold transition-all shadow-lg hover:shadow-orange-500/20 inline-flex items-center gap-2"
            >
              Start Free Mock Interview <ArrowRight size={18} />
            </button>
            <div className="flex flex-wrap justify-center gap-6 mt-6 text-sm text-slate-400">
              <span className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-emerald-500" /> No credit card</span>
              <span className="flex items-center gap-1.5"><Shield size={14} className="text-blue-400" /> GDPR compliant</span>
              <span className="flex items-center gap-1.5"><Clock size={14} className="text-amber-400" /> 15 min to complete</span>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
